import type { UploadItem } from "@/features/clipboard/hooks/useClipboardUploads";
import { X } from "lucide-react";

type ClipboardUploadTrayProps = {
  uploads: UploadItem[];
  onRetry: (item: UploadItem) => void;
  onDismiss: (item: UploadItem) => void;
};

export const ClipboardUploadTray = ({
  uploads,
  onRetry,
  onDismiss,
}: ClipboardUploadTrayProps) => {
  if (uploads.length === 0) return null;

  const uploading = uploads.filter((u) => u.status === "uploading");
  const transferring = uploading.filter((u) => u.phase !== "adding");
  const adding = uploading.filter((u) => u.phase === "adding");
  const failed = uploads.filter((u) => u.status === "error");
  const done = uploads.filter((u) => u.status === "done");
  const duplicates = done.filter((u) => u.message);
  const summary =
    transferring.length > 0
      ? `Uploading ${transferring.length} file${transferring.length > 1 ? "s" : ""}…`
      : adding.length > 0
        ? `Adding ${adding.length} file${adding.length > 1 ? "s" : ""}…`
        : failed.length > 0
          ? `${failed.length} file${failed.length > 1 ? "s" : ""} couldn’t be added`
          : duplicates.length === done.length && done.length > 0
            ? "Already in Clipboard"
            : "Added";

  return (
    <div
      className="mx-6 mb-2 rounded-[3px] border border-zinc-200 bg-white px-4 py-3 space-y-2"
      aria-live="polite"
    >
      <div className="flex items-center gap-2.5 text-sm text-zinc-600">
        {uploading.length > 0 ? (
          <span className="h-3.5 w-3.5 shrink-0 rounded-full border-2 border-zinc-300 border-t-zinc-700 animate-spin" />
        ) : failed.length > 0 ? (
          <span className="h-2 w-2 shrink-0 rounded-full bg-amber-500" />
        ) : (
          <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
        )}
        <span className="text-zinc-700">{summary}</span>
        {(done.length > 0 || failed.length > 0) && uploading.length > 0 && (
          <span className="text-sm text-zinc-400">
            {done.length > 0 && `${done.length} done`}
            {done.length > 0 && failed.length > 0 && " · "}
            {failed.length > 0 && `${failed.length} failed`}
          </span>
        )}
        <span className="ml-1 text-sm text-zinc-400">
          Added files remain available while processing continues.
        </span>
      </div>
      {failed.map((u) => (
        <div key={u.name} className="flex items-center gap-3 pl-6 text-sm">
          <span className="truncate text-zinc-600" title={u.name}>
            {u.name}
          </span>
          <span className="shrink-0 text-sm text-red-700">{u.message || "Failed"}</span>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => onRetry(u)}
              className="text-sm font-medium text-zinc-700 hover:text-zinc-900"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={() => onDismiss(u)}
              aria-label={`Dismiss ${u.name}`}
              className="text-zinc-300 hover:text-zinc-600"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};
